<template>
  <div class="ds-kpi-card" :class="'ds-kpi-card--' + tone">
    <div class="ds-kpi-card__head">
      <span class="ds-kpi-card__label" v-if="label">{{ label }}</span>
      <span class="ds-kpi-card__icon" v-if="$slots.icon"><slot name="icon"></slot></span>
    </div>

    <div class="ds-kpi-card__value-wrap" v-loading="loading" element-loading-text="加载中">
      <span class="ds-kpi-card__value" v-if="!loading">
        {{ displayValue }}<span class="ds-kpi-card__unit" v-if="unit">{{ unit }}</span>
      </span>
    </div>

    <div class="ds-kpi-card__foot" v-if="$slots.footer || (trend !== null && trend !== undefined)">
      <span class="ds-kpi-card__trend" v-if="trend !== null && trend !== undefined"
        :class="trend >= 0 ? 'is-up' : 'is-down'">
        <i :class="trend >= 0 ? 'el-icon-top' : 'el-icon-bottom'"></i>
        {{ Math.abs(trend) }}{{ trendUnit || '%' }}
      </span>
      <span class="ds-kpi-card__trend-label" v-if="trendLabel">{{ trendLabel }}</span>
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script>
import { formatNumber, formatMoneyCompact } from '@/utils/format'

/**
 * DsKpiCard —— 指标卡
 *
 * 重要（UI-REFACTOR-SPEC.md §10.6）：KPI 数值必须来自后端返回的
 * **全量统计字段**，严禁用分页/采样后的图表数据累加得出。
 * 本组件只负责展示，不做任何计算。
 *
 * Props:
 *  - label      {String}  指标名
 *  - value      {String|Number} 指标值
 *  - unit       {String}  单位（个 / 元 / % ...）
 *  - trend      {Number}  趋势百分比，正数向上负数向下；不传则不显示
 *  - trendUnit  {String}  趋势单位，默认 '%'
 *  - trendLabel {String}  趋势说明文字（如「较上月」）
 *  - tone       {String}  primary|success|warning|danger|info|muted
 *  - loading    {Boolean} 骨架/加载态
 *  - money      {Boolean} 是否按金额紧凑格式（万/亿）展示
 *  - decimals   {Number} 小数位
 *
 * Slots:
 *  - icon     右上角图标
 *  - footer   底部补充说明
 */
export default {
  name: 'DsKpiCard',
  props: {
    label: {
      type: String,
      default: ''
    },
    value: {
      type: [String, Number],
      default: ''
    },
    unit: {
      type: String,
      default: ''
    },
    trend: {
      type: Number,
      default: null
    },
    trendUnit: {
      type: String,
      default: '%'
    },
    trendLabel: {
      type: String,
      default: ''
    },
    tone: {
      type: String,
      default: 'primary'
    },
    loading: {
      type: Boolean,
      default: false
    },
    money: {
      type: Boolean,
      default: false
    },
    decimals: {
      type: Number,
      default: 2
    }
  },
  computed: {
    displayValue () {
      if (this.value === null || this.value === undefined || this.value === '') return '—'
      if (this.money) return formatMoneyCompact(this.value, { decimals: this.decimals })
      if (typeof this.value === 'number') return formatNumber(this.value, this.decimals)
      return this.value
    }
  }
}
</script>

<style lang="scss" scoped>
.ds-kpi-card {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  padding: var(--ds-spacing-md) var(--ds-spacing-lg);
  transition: box-shadow var(--ds-transition-base);
  min-height: 108px;

  &:hover {
    box-shadow: var(--ds-shadow-md);
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ds-spacing-sm);
  }

  &__label {
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-500);
    line-height: 1.4;
  }

  &__icon {
    color: var(--ds-gray-400);
    font-size: 16px;
  }

  &__value-wrap {
    min-height: 40px;
    display: flex;
    align-items: center;
  }

  &__value {
    font-size: var(--ds-font-size-3xl);
    font-weight: 700;
    line-height: 1.2;
    color: var(--ds-gray-900);
    font-family: var(--ds-font-family-num);
    font-variant-numeric: tabular-nums;
  }

  &__unit {
    font-size: var(--ds-font-size-sm);
    font-weight: 500;
    color: var(--ds-gray-500);
    margin-left: 4px;
  }

  &__foot {
    display: flex;
    align-items: center;
    gap: var(--ds-spacing-sm);
    margin-top: var(--ds-spacing-xs);
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-500);
  }

  &__trend {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-weight: 600;

    &.is-up {
      color: var(--ds-danger);
    }

    &.is-down {
      color: var(--ds-success);
    }
  }

  &--success .ds-kpi-card__value { color: var(--ds-success); }
  &--warning .ds-kpi-card__value { color: #B45309; }
  &--danger .ds-kpi-card__value { color: var(--ds-danger); }
  &--info .ds-kpi-card__value { color: var(--ds-info); }
  &--primary .ds-kpi-card__value { color: var(--ds-primary); }
}
</style>
